// BCM-898 · PA9 — IBD / GRN generation (slides 28, 29, 30). [Lavanya]
// PURE FLAG GATE: showGenerateGRNActionButton (GRN, PF2-5) or showCreateIBDActionButton
// (IBD, PF1), each plus its featuresAccess key. Per slide 28 the shipment quantity is
// entered INLINE on the dashboard (not in a popup): Ready for Dispatch Qty / UoM /
// Shipping all quantity / Date of Manufacture, a GRN Serial Number × Individual
// Shipment Quantity table with Add New Row, and an orange Generate button.
// GRN: the ASL check (slide 29) + SAP submission stay in the reused GenerateGRNDialog,
// opened on Generate with the inline quantities carried in.
// IBD: no ASL step, so Generate calls generateIBDWithConsumptionFromFE directly - the
// URL, payload and error-extraction below are a line-for-line copy of
// CreateIBDDialog.jsx's own handleSubmit (the same function Batch Execution's
// "Confirm Submission" button calls), so a failure here behaves identically to a
// failure there. CreateIBDDialog itself is untouched and still used unmodified by
// Batch Execution (SingleRow.jsx).
import React, { useState } from "react";
import axios from "axios";
import {
  Grid, Typography, Select, MenuItem, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, TextField, Button, Box, IconButton, Dialog, DialogContent,
  CircularProgress,
} from "@material-ui/core";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CloseIcon from "@material-ui/icons/Close";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import WarningIcon from "@material-ui/icons/Warning";
import DoneIcon from "@material-ui/icons/Done";
import SectionShell from "../components/SectionShell";
import GenerateGRNDialog from "../../sasl/deliveryScheduleBatchTable/GenerateGRNDialog";
import { appUrl } from "../../../../../../utils/config";
import { useGRStyles } from "../goodsReceiptStyles";
import { useGRFeedback } from "../context/GRFeedbackContext";
import { isFlagTrue, trimQty, formatDate, formatDateTime } from "../utils/formatters";
import { STATUS } from "../utils/constants";

// A quantity rendered the way the Batch Execution partial-shipment fields render
// it (3 dp, graceful fallback) — GenerateGRNDialog/CreateIBDDialog do the same.
const qty3dp = (value) =>
  value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value))
    ? Number(value).toFixed(3)
    : "-NA-";

// Copied from CreateIBDDialog.jsx's own deriveErrorType, unchanged.
const deriveErrorType = (errorMessage) => {
  if (!errorMessage) return '';
  const lowerMessage = errorMessage.toLowerCase();
  if (lowerMessage.includes('ibd not created') || lowerMessage.includes('i:ibd not created')) {
    return 'Generic IBD not created';
  }
  if (lowerMessage.includes('material') && lowerMessage.includes('not found')) {
    return 'Material not found in BOM';
  }
  return '';
};

const PA9GenerateGRNIBD = ({ row, featuresAccess, viewOnly, onRefresh }) => {
  const classes = useGRStyles();
  const isIBD = isFlagTrue(row.showCreateIBDActionButton);
  const readyQty = row.readyForDispatchQuantity || row.producedQuantity || 0;

  // A round of GRN/IBD generation has already run for this batch (statuses 60/61).
  // The same card then shows the partial-shipment context the Batch Execution
  // dialogs show — Qty. already dispatched / Last shipped at / Remaining Qty. —
  // and every quantity rule below switches from Ready-for-Dispatch to Remaining.
  const isPartial =
    row.SASLBatchStatusID === STATUS.PARTIAL_GRN ||
    row.SASLBatchStatusID === STATUS.PARTIAL_IBD ||
    row.statusDisplayName === "Partial GRN Generated" ||
    row.statusDisplayName === "Partial IBD Generated";
  const qtyAlreadyDispatched = row.qtyAlreadyDispatched;
  const remainingQty = row.remainingQtyTobeShipped || row.remainingQtyToBeShipped;
  // The quantity the individual shipment rows must add up to.
  const targetQty = isPartial ? remainingQty || 0 : readyQty;

  // Every header detail gets the same column width so they read as aligned columns.
  // With only four of them (no partial-shipment fields) they bunch up on the left,
  // so each column is widened to carry the row across to the shipment table's right
  // edge (4 × 162 + 3 × 24px gutters ≈ the table's 720px).
  // Four fields on their own either bunch up on the left (fixed widths) or stretch
  // the whole page (flexGrow with no cap), so that row is capped at the shipment
  // table's width and the four share it equally: 4 × 162 + 3 × 24px gutters = 720.
  // The 7-field partial row keeps sizing to its content — 720 would squash it.
  const headerField = isPartial ? { minWidth: 150 } : { minWidth: 0, flexGrow: 1 };
  // spacing={3} gives the container a -12px margin and each item 12px of side
  // padding, so the container must be 24px wider than the table for the outermost
  // content edges to line up with it.
  const headerRow = isPartial ? {} : { maxWidth: 744 };

  // Partial rounds start blank (and "No"): the planner types the quantity going out
  // in THIS round, which is rarely the whole remainder. A first round still
  // pre-fills the full Ready for Dispatch Qty, unchanged.
  const [shippingAll, setShippingAll] = useState(isPartial ? "No" : "Yes");
  const [rows, setRows] = useState([
    { id: 1, serial: 1, qty: isPartial ? "" : String(trimQty(readyQty) || "") },
  ]);
  const [grnOpen, setGrnOpen] = useState(false);
  const [grnGenerated, setGrnGenerated] = useState(false);
  const [ibdGenerated, setIbdGenerated] = useState(false);
  const [ibdSubmitting, setIbdSubmitting] = useState(false);
  const [ibdError, setIbdError] = useState({ open: false, message: "" });
  const feedback = useGRFeedback();

  const canGRN = !viewOnly && featuresAccess && featuresAccess.generateGRN !== false;
  const canIBD = !viewOnly && featuresAccess && featuresAccess.createIBD !== false;

  const addRow = () => setRows((r) => [...r, { id: (r[r.length - 1]?.id || 0) + 1, serial: r.length + 1, qty: "" }]);
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i).map((x, idx) => ({ ...x, serial: idx + 1 })));
  const setQty = (i, v) => setRows((r) => r.map((x, idx) => (idx === i ? { ...x, qty: v } : x)));

  const shipmentQuantities = rows.map((r) => Number(r.qty) || 0);
  const sum = shipmentQuantities.reduce((a, b) => a + b, 0);
  const sumMismatch = shippingAll === "Yes" && Math.abs(sum - Number(targetQty)) > 0.001;
  // EVERY row must carry a real quantity — a blank or a 0 in any row blocks Generate.
  // Same rule Batch Execution's own button uses (GenerateGRNDialog:
  // !shipmentRows.every(row => parseFloat(row.quantity) > 0)), so a half-filled
  // multi-row split is caught too, not just an empty first row. parseFloat("") is
  // NaN and NaN > 0 is false, so blanks fail the test alongside zeros.
  const noQtyEntered = !rows.every((r) => parseFloat(r.qty) > 0);
  // A typed 0 gets its own message; a row simply left blank does not (it only keeps
  // Generate disabled). Same distinction Batch Execution makes — GenerateGRNDialog's
  // hasZeroValue skips empty strings and reports only an explicit 0.
  const hasZeroValue = rows.some(
    (r) => typeof r.qty === "string" && r.qty.trim() !== "" && parseFloat(r.qty) === 0
  );
  // "No" = only part of what is available ships now, but it still cannot exceed what
  // is left. Batch Execution enforces the same rule ("cannot exceed Remaining Qty.");
  // PA9 previously validated the "Yes" branch only, so an over-shipment went through.
  const exceedsTarget =
    shippingAll === "No" && Number(targetQty) > 0 && sum - Number(targetQty) > 0.001;

  // Note 3 (mirrors GenerateGRNDialog.jsx's own "Generating GRN" step, which this
  // inline section now replaces for Goods Receipt): a per-row max posting quantity,
  // only enforced when the shipment configuration for this product is active.
  const hasMaxPostingQty = row.isActive === true && row.maxPostingQtyAllowed !== undefined
    && row.maxPostingQtyAllowed !== null && Number(row.maxPostingQtyAllowed) > 0;
  const maxPostingQtyUom = row.targetQuantityUOM || row.batchUOM || "";
  const maxQtyExceeded = hasMaxPostingQty
    && rows.some((r) => r.qty !== "" && Number(r.qty) > Number(row.maxPostingQtyAllowed));

  // Carry the inline entries into the reused dialog for the ASL check + submission.
  const batchData = {
    ...row,
    vendorMosymphonyID: row.vendorMosymphonyID,
    schrockenDeliveryScheduleBatchID: row.schrockenDeliveryScheduleBatchID,
    poNumber: row.scheduleAgreementNumber,
    batchNumber: row.vendorBatchNumber,
    SASLBatchStatusID: row.SASLBatchStatusID,
    statusDisplayName: row.statusDisplayName,
    readyForDispatchQuantity: readyQty,
    allQuantitiesShipped: shippingAll === "Yes",
    shipmentQuantities,
    qtyAlreadyDispatched,
    lastShippedDate: row.lastShippedDate,
    remainingQtyTobeShipped: remainingQty,
  };

  // GRN success (slide 31): "4-second toast, flip the button, refetch, re-resolve" -
  // flip the button first so it's visible alongside the toast, then let the shared
  // GRFeedbackContext toast + poll-until-status-changes drive the refetch.
  const handleGrnSuccess = () => {
    setGrnOpen(false);
    setGrnGenerated(true);
    feedback.notifySuccess({
      message: "ASL information submitted to SAP; GRN generated successfully",
      statusChanging: true,
      batchId: row.schrockenDeliveryScheduleBatchID,
      prevStatus: row.SASLBatchStatusID,
      refetch: onRefresh,
    });
  };

  const handleIbdResult = (res) => {
    if (res && res.success === false) {
      // Failure shows the same blocking dialog Batch Execution shows; the batch
      // does not advance.
      setIbdError({ open: true, message: res.errorMessage || "IBD generation failed." });
      return;
    }
    // Success: flip the button to "IBD Generated" (visible alongside the toast),
    // then the shared GRFeedbackContext toast + poll-until-status-changes drives
    // the refetch once the toast closes.
    setIbdGenerated(true);
    feedback.notifySuccess({
      message: "IBD generated successfully",
      statusChanging: true,
      batchId: row.schrockenDeliveryScheduleBatchID,
      prevStatus: row.SASLBatchStatusID,
      refetch: onRefresh,
    });
  };

  const closeIbdError = () => {
    setIbdError({ open: false, message: "" });
    if (onRefresh) onRefresh();
  };

  // Copy of CreateIBDDialog.jsx's handleSubmit (same URL, same payload, same
  // response/error handling) - called directly on Generate instead of via that
  // dialog's "Confirm Submission" button, since the shipment quantities are already
  // entered inline above.
  const submitIbd = async () => {
    const shipmentQuantitiesToSend = batchData.shipmentQuantities.filter((q) => q > 0);
    if (shipmentQuantitiesToSend.length === 0) {
      console.error('Please enter at least one valid shipment quantity');
      return;
    }

    setIbdSubmitting(true);

    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/generateIBDWithConsumptionFromFE`;

      const payload = {
        schrockenDeliveryScheduleBatchID: batchData?.schrockenDeliveryScheduleBatchID,
        poNumber: batchData?.poNumber,
        batchNumber: batchData?.batchNumber,
        allQuantitiesShipped: batchData.allQuantitiesShipped,
        shipmentQuantities: shipmentQuantitiesToSend,
      };

      const response = await axios.post(url, payload);

      if (response.data.success) {
        handleIbdResult({ success: true, message: 'IBD created successfully' });
      } else {
        console.error("IBD creation failed:", response.data);

        // Extract error details from response
        let errorType = '';
        let errorMessage = '';

        // Check for new response structure: response.data.sapResponse (array)
        if (response.data.sapResponse && Array.isArray(response.data.sapResponse) && response.data.sapResponse.length > 0) {
          const sapResponse = response.data.sapResponse[0];

          // Get error message from sapResponse Output
          if (sapResponse.Output && sapResponse.Output.item && sapResponse.Output.item.length > 0) {
            errorMessage = sapResponse.Output.item[0].ErrorMsg || '';
          }

          // Derive user-friendly error type
          errorType = deriveErrorType(errorMessage);

          // If no derived error type and no message, use fallback
          if (!errorType && !errorMessage) {
            errorMessage = 'IBD not created Bad Request : 400 : HTTP/1.1';
            errorType = 'Generic IBD not created';
          }
        }
        // Fallback to old structure: response.data.data.failedShipments
        else if (response.data.data && response.data.data.failedShipments && response.data.data.failedShipments.length > 0) {
          const failedShipment = response.data.data.failedShipments[0];

          // Get error message from sapResponse
          if (failedShipment.sapResponse && failedShipment.sapResponse.Output && failedShipment.sapResponse.Output.item && failedShipment.sapResponse.Output.item.length > 0) {
            errorMessage = failedShipment.sapResponse.Output.item[0].ErrorMsg || '';
          }

          // Derive user-friendly error type
          errorType = deriveErrorType(errorMessage);

          // If no derived error type and no message, use fallback
          if (!errorType && !errorMessage) {
            errorMessage = 'IBD not created Bad Request : 400 : HTTP/1.1';
            errorType = 'Generic IBD not created';
          }
        }

        handleIbdResult({
          success: false,
          message: 'IBD creation failed',
          errorType: errorType,
          errorMessage: errorMessage,
        });
      }
    } catch (error) {
      console.error("Error creating IBD:", error);

      let errorType = '';
      let errorMessage = '';

      if (error.response && error.response.data) {
        // Extract error details from error response
        // Check for new response structure: error.response.data.sapResponse (array)
        if (error.response.data.sapResponse && Array.isArray(error.response.data.sapResponse) && error.response.data.sapResponse.length > 0) {
          const sapResponse = error.response.data.sapResponse[0];

          // Get error message from sapResponse Output
          if (sapResponse.Output && sapResponse.Output.item && sapResponse.Output.item.length > 0) {
            errorMessage = sapResponse.Output.item[0].ErrorMsg || '';
          }

          // Derive user-friendly error type
          errorType = deriveErrorType(errorMessage);
        }
        // Fallback to old structure: error.response.data.data.failedShipments
        else if (error.response.data.data && error.response.data.data.failedShipments && error.response.data.data.failedShipments.length > 0) {
          const failedShipment = error.response.data.data.failedShipments[0];

          // Get error message from sapResponse
          if (failedShipment.sapResponse && failedShipment.sapResponse.Output && failedShipment.sapResponse.Output.item && failedShipment.sapResponse.Output.item.length > 0) {
            errorMessage = failedShipment.sapResponse.Output.item[0].ErrorMsg || '';
          }

          // Derive user-friendly error type
          errorType = deriveErrorType(errorMessage);
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
          errorType = deriveErrorType(errorMessage);
        }

        // If no derived error type and no message, use fallback based on status code
        if (!errorType && !errorMessage) {
          const statusCode = error.response.status;
          errorMessage = `IBD not created Bad Request : ${statusCode} : HTTP/1.1`;
          errorType = 'Generic IBD not created';
        }
      }

      handleIbdResult({
        success: false,
        message: 'IBD creation failed',
        errorType: errorType,
        errorMessage: errorMessage,
      });
    } finally {
      setIbdSubmitting(false);
    }
  };

  const onGenerate = () => (isIBD ? submitIbd() : setGrnOpen(true));

  return (
    <SectionShell viewOnly={viewOnly}>
      {/* Header details sit in even columns (fixed min width each) so the labels and
          values line up as a tidy row above the shipment table instead of each field
          sizing to its own text. */}
      <Grid container spacing={3} style={{ marginBottom: "1rem", ...headerRow }}>
        <Grid item style={headerField}><Typography className={classes.pa9FieldLabel}>Ready for Dispatch Qty.</Typography><Typography className={classes.pa9FieldValue}>{trimQty(readyQty)}</Typography></Grid>
        {isPartial && (
          <>
            <Grid item style={headerField}><Typography className={classes.pa9FieldLabel}>Qty. already dispatched</Typography><Typography className={classes.pa9FieldValue}>{qty3dp(qtyAlreadyDispatched)}</Typography></Grid>
            <Grid item style={headerField}><Typography className={classes.pa9FieldLabel}>Last shipped at</Typography><Typography className={classes.pa9FieldValue}>{formatDateTime(row.lastShippedDate)}</Typography></Grid>
            <Grid item style={headerField}><Typography className={classes.pa9FieldLabel}>Remaining Qty.</Typography><Typography className={classes.pa9FieldValue}>{qty3dp(remainingQty)}</Typography></Grid>
          </>
        )}
        <Grid item style={headerField}><Typography className={classes.pa9FieldLabel}>UoM</Typography><Typography className={classes.pa9FieldValue}>{row.batchUOM}</Typography></Grid>
        <Grid item style={headerField}>
          <Typography className={classes.pa9FieldLabel}>Shipping all {isPartial ? "remaining " : ""}quantity</Typography>
          <Select
            value={shippingAll}
            onChange={(e) => {
              const value = e.target.value;
              setShippingAll(value);
              // Yes = the whole (remaining) quantity ships in one go, so pre-fill it.
              // No = the planner types what is actually going out now, so start blank
              // rather than leaving the full quantity sitting there. Same handler
              // GenerateGRNDialog uses on Batch Execution.
              setRows([
                { id: 1, serial: 1, qty: value === "Yes" ? String(trimQty(targetQty) || "") : "" },
              ]);
            }}
            variant="outlined"
            margin="dense"
            style={{ minWidth: 100 }}
          >
            <MenuItem value="Yes">Yes</MenuItem>
            <MenuItem value="No">No</MenuItem>
          </Select>
        </Grid>
        <Grid item style={headerField}><Typography className={classes.pa9FieldLabel}>Date of Manufacture</Typography><Typography className={classes.pa9FieldValue}>{formatDate(row.manufacturingDate)}</Typography></Grid>
      </Grid>

      <TableContainer component={Paper} style={{ boxShadow: "none", border: "1px solid #E0E4EA", maxWidth: 720 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableCellHead}>{isIBD ? "IBD" : "GRN"} Serial Number</TableCell>
              <TableCell className={classes.tableCellHead}>Individual Shipment Quantity</TableCell>
              <TableCell className={classes.tableCellHead} align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={r.id}>
                <TableCell>{r.serial}</TableCell>
                <TableCell>
                  <TextField value={r.qty} onChange={(e) => setQty(i, e.target.value)} variant="outlined" size="small" type="number" fullWidth />
                </TableCell>
                <TableCell align="center">
                  {rows.length > 1 && (
                    <IconButton size="small" onClick={() => removeRow(i)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" style={{ display: "block", color: "#6B7280", marginTop: 8 }}>
        Note 1 - If Shipping all {isPartial ? "remaining " : ""}quantity = YES, sum of all Individual Shipment Size should match {isPartial ? "Remaining Qty." : "Ready for Dispatch Qty."}
      </Typography>
      <Typography variant="caption" style={{ display: "block", color: "#6B7280" }}>
        Note 2 - Consumed batch quantity is assigned proportionately to each {isIBD ? "IBD" : "GRN"} Serial Number, based on the Individual Shipment Size.
      </Typography>
      {hasMaxPostingQty && (
        <Typography variant="caption" style={{ display: "block", color: "#6B7280" }}>
          Note 3 - Under shipment configuration, this Product is allowed maximum posting quantity of {Number(row.maxPostingQtyAllowed).toLocaleString("en-US")} {maxPostingQtyUom} only.
        </Typography>
      )}
      <Box mt={2} className={classes.btnRow}>
        <Button className={classes.outlinedBtn} startIcon={<AddCircleOutlineIcon />} onClick={addRow} disabled={viewOnly}>
          Add New Row
        </Button>
      </Box>

      {(hasZeroValue || sumMismatch || exceedsTarget || maxQtyExceeded) && (
        <Box mt={2} style={{
          border: '1px solid #f44336',
          backgroundColor: '#ffebee',
          color: '#d32f2f',
          display: 'flex',
          alignItems: 'center',
          padding: '12px 16px',
          borderRadius: '8px',
        }}>
          <WarningIcon style={{ color: '#f44336', marginRight: '12px', fontSize: '24px' }} />
          <Typography style={{ fontSize: '14px', fontWeight: 500 }}>
            {hasZeroValue
              ? "You have entered 0 value. Please enter a valid Individual Shipment Quantity."
              : sumMismatch
              ? `Sum of individual shipment quantities (${trimQty(sum)}) must equal ${isPartial ? "Remaining Qty" : "Ready for Dispatch Qty"} (${trimQty(targetQty)}).`
              : exceedsTarget
              ? `Sum of individual shipment quantities (${trimQty(sum)}) cannot exceed ${isPartial ? "Remaining Qty" : "Ready for Dispatch Qty"} (${trimQty(targetQty)}).`
              : `Individual Shipment Quantity cannot exceed the maximum posting quantity of ${Number(row.maxPostingQtyAllowed).toLocaleString("en-US")} ${maxPostingQtyUom}.`}
          </Typography>
        </Box>
      )}

      <Box mt={3} display="flex" justifyContent="center">
        {!isIBD && grnGenerated ? (
          <Button
            className={classes.grnGeneratedBtn}
            disabled
            disableElevation
            endIcon={<DoneIcon className={classes.grnGeneratedIcon} style={{ fontSize: 22 }} />}
          >
            GRN Generated
          </Button>
        ) : isIBD && ibdGenerated ? (
          <Button
            className={classes.grnGeneratedBtn}
            disabled
            disableElevation
            endIcon={<DoneIcon className={classes.grnGeneratedIcon} style={{ fontSize: 22 }} />}
          >
            IBD Generated
          </Button>
        ) : (
          <Button
            className={classes.orangeBtn}
            onClick={onGenerate}
            disabled={
              (isIBD ? !canIBD : !canGRN) ||
              noQtyEntered ||
              sumMismatch ||
              exceedsTarget ||
              maxQtyExceeded ||
              ibdSubmitting
            }
            startIcon={ibdSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {isIBD ? (ibdSubmitting ? "Creating IBD..." : "Create IBD") : "Generate GRN"}
          </Button>
        )}
      </Box>

      <GenerateGRNDialog
        key={`grn-${row.schrockenDeliveryScheduleBatchID}`}
        open={grnOpen}
        onClose={() => { setGrnOpen(false); if (onRefresh) onRefresh(); }}
        batchData={batchData}
        isFromGoodsReceipt
        onSuccess={handleGrnSuccess}
      />

      {/* Same failure dialog Batch Execution shows (SingleRow.jsx's ibdDialog, failure
          branch) - red circle, bold title, subtext, error-message bullet, Jira link.
          Closing it also refreshes the row, same as Batch Execution's unconditional
          onRefreshData() in handleIbdDialogClose. */}
      <Dialog open={ibdError.open} fullWidth onClose={closeIbdError}>
        <DialogContent style={{ padding: "2rem" }}>
          <Grid container justifyContent="flex-end">
            <Typography
              variant="body2"
              style={{ fontSize: "1rem", fontWeight: 500, display: "flex", alignItems: "center", cursor: "pointer" }}
              onClick={closeIbdError}
            >
              Close <CloseIcon fontSize="large" />
            </Typography>
            <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
              <div style={{ width: "5rem", height: "5rem", borderRadius: "50%", backgroundColor: "#F00", display: "flex", alignItems: "center", justifyContent: "center", color: "white" }}>
                <CloseIcon style={{ fontSize: "3rem", color: "white" }} />
              </div>
            </Grid>
          </Grid>
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", minHeight: "80px", padding: "1rem" }}>
            <Typography style={{ textAlign: "center", fontWeight: "bold", fontSize: "1.5rem", marginBottom: "0.5rem" }}>
              IBD generation failed
            </Typography>
            <Typography style={{ textAlign: "left", fontSize: "1rem", marginTop: "0.5rem", color: "#666", width: "100%" }}>
              There was some error in IBD Details submission. Please try again later.
            </Typography>
            <ul style={{ textAlign: "left", marginTop: "12px", paddingLeft: "20px", listStyleType: "disc", width: "100%" }}>
              {ibdError.message && (
                <li style={{ marginBottom: "8px" }}>
                  <Typography component="span" style={{ fontSize: "1rem", color: "#333" }}>
                    Error message as received - {ibdError.message}
                  </Typography>
                </li>
              )}
            </ul>
            <Typography style={{ textAlign: "left", fontSize: "1rem", marginTop: "1rem", color: "#666", width: "100%" }}>
              Jira link for issue - <a href="https://collabrix-jsm.atlassian.net/servicedesk/customer/portal/1/group/34/create/67" target="_blank" rel="noopener noreferrer">https://collabrix-jsm.atlassian.net/servicedesk/customer/portal/1/group/34/create/67</a>
            </Typography>
          </div>
          <Grid item xs={12} style={{ display: "flex", justifyContent: "center", textAlign: "center", padding: "0.5rem" }}>
            <Button variant="contained" color="primary" endIcon={<ChevronRightIcon />} onClick={closeIbdError} style={{ paddingLeft: "3rem", paddingRight: "3rem" }}>
              OK
            </Button>
          </Grid>
        </DialogContent>
      </Dialog>
    </SectionShell>
  );
};

export default PA9GenerateGRNIBD;
